import React, { useState, useEffect } from 'react';
import { documentsApi } from '../services/api';
import { DocumentDto } from '@shared/contracts';
import { FileText, Users, ChevronDown, ChevronUp, RefreshCw, Clock, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

interface UserJobGroup {
  userId: string;
  userEmail: string;
  jobsCount: number;
  documents: DocumentDto[];
}

export const JobsByUserPage: React.FC = () => {
  const [groupedJobs, setGroupedJobs] = useState<UserJobGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const fetchGroupedJobs = async () => {
    setLoading(true);
    try {
      const data = await documentsApi.getJobsGroupedByUser();
      setGroupedJobs(data || []);
      if (data && data.length > 0 && !expandedUser) {
        setExpandedUser(data[0].userId);
      }
    } catch (e) {
      console.error('Failed to fetch jobs by user', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupedJobs();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            <span>COMPLETED</span>
          </span>
        );
      case 'PENDING_REVIEW':
        return (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
            <AlertTriangle className="w-3 h-3 text-amber-600" />
            <span>PENDING REVIEW</span>
          </span>
        );
      case 'FAILED':
        return (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
            <XCircle className="w-3 h-3 text-rose-600" />
            <span>FAILED</span>
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-200">
            <Clock className="w-3 h-3 text-sky-600 animate-spin" />
            <span>{status}</span>
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto font-sans">
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-600" />
            <span>Jobs List by User</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Super Admin View: Overview of document processing jobs broken down per user account.
          </p>
        </div>

        <button
          onClick={fetchGroupedJobs}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs font-semibold transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-sky-600' : ''}`} />
          <span>Refresh Jobs</span>
        </button>
      </div>

      {loading ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center text-xs text-slate-500">
          Loading jobs broken down by user...
        </div>
      ) : groupedJobs.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-2">
          <Users className="w-8 h-8 text-slate-300 mx-auto" />
          <h3 className="text-sm font-bold text-slate-700">No User Jobs Found</h3>
          <p className="text-xs text-slate-500">No document processing jobs have been submitted yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedJobs.map((group) => {
            const isExpanded = expandedUser === group.userId;
            return (
              <div
                key={group.userId}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all"
              >
                <button
                  onClick={() => setExpandedUser(isExpanded ? null : group.userId)}
                  className="w-full p-5 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-700 font-bold text-sm">
                      {group.userEmail.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">{group.userEmail}</h3>
                      <p className="text-[11px] text-slate-400 font-mono">{group.userId}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                      {group.jobsCount} {group.jobsCount === 1 ? 'Job' : 'Jobs'}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50/50">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs text-slate-700 min-w-[600px]">
                        <thead className="bg-white text-slate-500 uppercase tracking-wider text-[10px] font-semibold border-b border-slate-200">
                          <tr>
                            <th className="py-2.5 px-3 whitespace-nowrap">Job ID</th>
                            <th className="py-2.5 px-3 whitespace-nowrap">File Name</th>
                            <th className="py-2.5 px-3 whitespace-nowrap">Category / ISO</th>
                            <th className="py-2.5 px-3 whitespace-nowrap">Status</th>
                            <th className="py-2.5 px-3 whitespace-nowrap">Created At</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {group.documents.map((doc) => (
                            <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-2.5 px-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                                {doc.id}
                              </td>
                              <td className="py-2.5 px-3 font-medium text-slate-900 whitespace-nowrap">
                                {doc.originalName}
                              </td>
                              <td className="py-2.5 px-3 whitespace-nowrap">
                                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                                  {doc.documentCategory || 'IDENTITY'} ({doc.countryIso || 'US'})
                                </span>
                              </td>
                              <td className="py-2.5 px-3 whitespace-nowrap">{getStatusBadge(doc.status)}</td>
                              <td className="py-2.5 px-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                                {new Date(doc.createdAt).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
